import type { HypotheticalFlowDataRow, HypotheticalFlowResultStatus } from ".";

function numDigits(x: number): number {
  return Math.max(Math.floor(Math.log10(Math.abs(x))), 0) + 1;
}

export const doOneRow = (
  hypotheticalFlowRateSTBD: number,
  producedPressurePSIA: number,
  requiredPressurePSIA: number,
  depth: number,
  maxDeltaRequiredPressure: number,
  maxDepth: number,
  pumpOutOfRange: boolean,
  previousRow?: HypotheticalFlowDataRow
): HypotheticalFlowDataRow => {
  const previousChange = previousRow?.pressureDifference;
  const pressureDifference = producedPressurePSIA - requiredPressurePSIA;
  const pressureDifferenceChange = previousChange
    ? pressureDifference - previousChange
    : undefined;
  const pressureDifferenceHasGrown = !!(
    pressureDifferenceChange && pressureDifferenceChange > 0
  );
  const pressureDifferenceHasShrunk = !!(
    pressureDifferenceChange && pressureDifferenceChange < 0
  );

  // Calcs can randomly yield numbers that are extremely high that we
  // know to be invalid. We need to make sure the produced pressure value
  // yielded is within range of the required
  // pressure and not in the millions/billions/etc
  const requiredPressurePSIADigits = numDigits(requiredPressurePSIA);
  const producedPressurePSIADigits = numDigits(producedPressurePSIA);

  // If flow for the pump is out of range, it will produce unreliable
  // results which throws the whole algo out of whack. This is now
  // included in the health check for each row.
  const isHealthy =
    producedPressurePSIA > requiredPressurePSIA && !pumpOutOfRange;

  const withinTolerance =
    isHealthy &&
    producedPressurePSIA - requiredPressurePSIA < maxDeltaRequiredPressure;

  // depth basically translates to the log10 power currently at play
  // the search can start at 150k, with a step size of 10,000
  // MAX_DEPTH is set to 11 to ensure that the algorithm will reach beyond
  // .0001 precision

  let status: HypotheticalFlowResultStatus = "seeking-threshold";
  const previousStatus = previousRow?.status || "seeking-threshold";
  if (previousStatus === "threshold-found") {
    // special logic here - probably completely parallel to the big if switch below
    if (isHealthy && withinTolerance && pressureDifferenceHasShrunk) {
      status = "threshold-found";
    } else if (isHealthy && pressureDifferenceHasGrown) {
      status = "threshold-confirmed";
    } else if (!isHealthy) {
      // this is the current case i think
      // flowRate   produced            required            pressureDifference pressureDifferenceChange isHealthy withinTolerance depth status
      // 600	      2097.9029244656963	1808.8075791244312	289.0953453412651		                        true	    false	           1	   threshold-found
      // 590	      2263.320852404818	  2667.819087814042	  -404.498235409224	  -693.5935807504891	    false	    false	           1	   seeking-threshold
      status = "seeking-threshold";
    }
  } else {
    if (pumpOutOfRange) {
      status = "error: pump out of range";
    } else if (
      !isFinite(producedPressurePSIA) ||
      !isFinite(requiredPressurePSIA) ||
      producedPressurePSIA < 0 ||
      requiredPressurePSIA < 0 ||
      producedPressurePSIADigits > requiredPressurePSIADigits + 1
    ) {
      status = "error: cannot calculate pressure difference";
    } else if (isHealthy && withinTolerance) {
      status = "result-found";
    } else if (isHealthy && !withinTolerance) {
      if (pressureDifferenceHasGrown && depth > maxDepth) {
        status = "error: cannot find healthy flow within tolerance";
      } else if (
        pressureDifferenceHasGrown &&
        depth <= maxDepth &&
        (!previousChange || previousChange >= 0)
      ) {
        status = "seeking-threshold";
      } else if (
        pressureDifference > 0 &&
        depth <= maxDepth &&
        (!previousChange || previousChange < 0)
      ) {
        status = "threshold-found";
      } else {
        status = "seeking-tolerance";
      }
    } else {
      status = "seeking-threshold";
    }
  }

  return {
    hypotheticalFlowRateSTBD,
    producedPressurePSIA,
    requiredPressurePSIA,
    pressureDifference,
    pressureDifferenceChange,
    pressureDifferenceHasGrown,
    isHealthy,
    withinTolerance,
    depth,
    status
  };
};
